import { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import L from 'leaflet';
import { getJobsiteCoords } from './useJobsite';

/**
 * Loads and renders property lines for a jobsite.
 * Sources (in priority order):
 *   1. Backend getAPNAndParcelInfo (AspireProperty / parcel data)
 *   2. Manual property lines (user-drawn overrides)
 *   3. ESRI Maricopa County fallback (direct point-in-polygon query)
 *
 * Also supports drawing new custom property lines.
 */
export default function usePropertyLines({ url, jobsite, mapRef, visible = true }) {
  const [countyLines, setCountyLines] = useState([]);
  const [manualLines, setManualLines] = useState([]);
  const [usingFallback, setUsingFallback] = useState(false);
  const [countyVisible, setCountyVisible] = useState(true);
  const [manualVisible, setManualVisible] = useState(true);
  const layerGroupRef = useRef(null);

  // Create feature group once
  useEffect(() => {
    const map = mapRef.current;
    if (!map || layerGroupRef.current) return;
    layerGroupRef.current = new L.FeatureGroup();
    map.addLayer(layerGroupRef.current);
    return () => {
      if (layerGroupRef.current) {
        try { map.removeLayer(layerGroupRef.current); } catch (_) {}
        layerGroupRef.current = null;
      }
    };
  }, [mapRef]);

  // Load county + manual property lines for a jobsite
  const loadLines = useCallback(async () => {
    if (!url || !jobsite?._id) {
      setCountyLines([]);
      setManualLines([]);
      setUsingFallback(false);
      return;
    }
    const siteId = jobsite._id;
    try {
      const resp = await axios.get(
        `${url}/api/customer/getAPNAndParcelInfo?id=${siteId}&includeManual=true`
      );

      const county = [];
      const data = resp?.data;

      if (data?.propertyLines?.length) {
        data.propertyLines
          .filter(p => p?.googleMapsFormat?.length > 0)
          .forEach((property, idx) => {
            const props = property.properties || {};
            county.push({
              id: `county_${siteId}_${props.APN || idx}`,
              APN: props.APN,
              paths: property.googleMapsFormat.map(p => ({ lat: p.lat, lng: p.lng })),
              source: 'county',
            });
          });
      } else if (data?.length && data[0]?.googleMapsFormat) {
        const p = data[0];
        county.push({
          id: `county_${siteId}_${p.APN || 0}`,
          APN: p.APN,
          paths: p.googleMapsFormat.map(pt => ({ lat: pt.lat, lng: pt.lng })),
          source: 'county',
        });
      }

      setCountyLines(county);
      setUsingFallback(false);

      // If nothing returned, try ESRI fallback
      if (county.length === 0) {
        const coords = getJobsiteCoords(jobsite);
        if (coords) {
          const fallback = await fetchESRIFallback(siteId, coords[0], coords[1]);
          if (fallback) {
            setCountyLines([fallback]);
            setUsingFallback(true);
          }
        }
      }

      // Load manual lines
      const manualResp = await axios.get(
        `${url}/api/customer/manual-property-lines?jobSiteId=${siteId}`
      );
      if (manualResp.data?.success && Array.isArray(manualResp.data.lines)) {
        setManualLines(manualResp.data.lines);
      } else {
        setManualLines([]);
      }
    } catch (err) {
      console.error('Error loading property lines:', err);
      // Try fallback on error too
      try {
        const coords = getJobsiteCoords(jobsite);
        if (coords) {
          const fallback = await fetchESRIFallback(siteId, coords[0], coords[1]);
          if (fallback) {
            setCountyLines([fallback]);
            setUsingFallback(true);
          }
        }
      } catch (_) {}
    }
  }, [url, jobsite]);

  useEffect(() => { loadLines(); }, [loadLines]);

  // Render property lines — respects per-type visibility
  useEffect(() => {
    const map = mapRef.current;
    const group = layerGroupRef.current;
    if (!map || !group) return;
    group.clearLayers();

    if (!visible) return;

    if (countyVisible) {
      countyLines.forEach((line) => {
        if (!line.paths?.length) return;
        const coords = line.paths.map(p => [p.lat, p.lng]);
        const layer = L.polygon(coords, {
          color: '#4caf50',
          weight: 3,
          opacity: 0.8,
          fillColor: '#4caf50',
          fillOpacity: 0.05,
          interactive: false,
        });
        if (line.APN) layer.bindTooltip(`APN: ${line.APN}`);
        group.addLayer(layer);
      });
    }

    if (manualVisible) {
      manualLines.forEach((line) => {
        if (!line.paths?.length || line.paths.length < 3) return;
        const coords = line.paths.map(p => [p.lat, p.lng]);
        const layer = L.polygon(coords, {
          color: '#2e7d32',
          weight: 3,
          opacity: 0.9,
          fillColor: '#4caf50',
          fillOpacity: 0.08,
          dashArray: '6, 4',
          interactive: false,
        });
        layer.bindTooltip(`<strong>${line.name || 'Custom Property Line'}</strong><br/><em>Manual</em>`);
        group.addLayer(layer);
      });
    }
  }, [countyLines, manualLines, visible, countyVisible, manualVisible, mapRef]);

  // Draw a new custom property line — saves to backend
  const saveManualLine = useCallback(async (paths) => {
    if (!url || !jobsite?._id || !paths || paths.length < 3) return null;
    try {
      const resp = await axios.post(
        `${url}/api/customer/manual-property-lines`,
        {
          jobSiteId: jobsite._id,
          name: 'Custom Property Line',
          paths,
        }
      );
      if (resp.data?.success && resp.data?.line) {
        setManualLines(prev => [...prev, resp.data.line]);
        return resp.data.line;
      }
    } catch (err) {
      console.error('saveManualLine error:', err);
    }
    return null;
  }, [url, jobsite]);

  const deleteManualLine = useCallback(async (lineId) => {
    if (!url || !lineId) return;
    try {
      await axios.delete(`${url}/api/customer/manual-property-lines/${lineId}`);
      setManualLines(prev => prev.filter(l => l._id !== lineId));
    } catch (err) {
      console.error('deleteManualLine error:', err);
    }
  }, [url]);

  const renameManualLine = useCallback(async (lineId, newName) => {
    if (!url || !lineId) return;
    try {
      const resp = await axios.put(
        `${url}/api/customer/manual-property-lines/${lineId}`,
        { name: newName }
      );
      if (resp.data?.success) {
        setManualLines(prev => prev.map(l => l._id === lineId ? { ...l, name: newName } : l));
      }
    } catch (err) {
      console.error('renameManualLine error:', err);
    }
  }, [url]);

  const updateManualLinePaths = useCallback(async (lineId, newPaths) => {
    if (!url || !lineId || !newPaths || newPaths.length < 3) return;
    try {
      const resp = await axios.put(
        `${url}/api/customer/manual-property-lines/${lineId}`,
        { paths: newPaths }
      );
      if (resp.data?.success) {
        setManualLines(prev => prev.map(l => l._id === lineId ? { ...l, paths: newPaths } : l));
      }
    } catch (err) {
      console.error('updateManualLinePaths error:', err);
    }
  }, [url]);

  return {
    countyLines,
    manualLines,
    usingFallback,
    countyVisible,
    manualVisible,
    setCountyVisible,
    setManualVisible,
    saveManualLine,
    deleteManualLine,
    renameManualLine,
    updateManualLinePaths,
    reload: loadLines,
  };
}

// ESRI fallback — same as V1
async function fetchESRIFallback(siteId, lat, lng) {
  if (typeof lat !== 'number' || typeof lng !== 'number') return null;
  try {
    const parcelsUrl = 'https://gis.mcassessor.maricopa.gov/arcgis/rest/services/Parcels/MapServer/0/query';
    const params = {
      f: 'geojson',
      geometry: `${lng},${lat}`,
      geometryType: 'esriGeometryPoint',
      inSR: '4326',
      outSR: '4326',
      spatialRel: 'esriSpatialRelIntersects',
      returnGeometry: 'true',
      outFields: '*',
    };
    const qs = Object.entries(params)
      .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
      .join('&');
    const resp = await axios.get(`${parcelsUrl}?${qs}`);
    const features = resp?.data?.features;
    if (!features?.length) return null;

    const geom = features[0].geometry;
    if (!geom || geom.type !== 'Polygon' || !geom.coordinates?.[0]?.length) return null;

    const paths = geom.coordinates[0].map(([lng2, lat2]) => ({ lat: lat2, lng: lng2 }));
    const props = features[0].properties || {};

    return {
      id: `esri_${siteId}_${props.APN || props.OBJECTID || 'unknown'}`,
      APN: props.APN || '',
      paths,
      source: 'esri_fallback',
    };
  } catch (err) {
    console.warn('ESRI fallback failed:', err?.message || err);
    return null;
  }
}
